import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import axios from "axios";
import { hostname, parseError } from "../utils";
import { Department } from "../dashboard/settings/Departments";

interface DepartmentSelectorProps {
  companyId: string;
  getToken: () => Promise<string>;
  selectedDepartmentIds: string[];
  onChange: (departmentIds: string[]) => void;
  eventListener?: Function;
  label?: string;
  helperText?: string;
  style?: React.CSSProperties;
}

const DepartmentSelector: React.FC<DepartmentSelectorProps> = ({
  companyId,
  getToken,
  selectedDepartmentIds,
  onChange,
  eventListener,
  label = "Departments",
  helperText,
  style,
}) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDepartments();
  }, [companyId]);

  const loadDepartments = async () => {
    setLoading(true);
    const currentToken = await getToken();

    try {
      const response = await axios.get(
        `${hostname}/departments?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setDepartments(response.data);
    } catch (err: any) {
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange(typeof value === "string" ? value.split(",") : value);
  };

  // Don't show selector if no departments exist
  if (departments.length === 0 && !loading) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <Box style={style} display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading departments...
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl fullWidth style={style}>
      <InputLabel id="department-selector-label">{label}</InputLabel>
      <Select
        labelId="department-selector-label"
        multiple
        value={selectedDepartmentIds}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((id) => {
              const dept = departments.find((d) => d.id === id);
              return (
                <Chip
                  key={id}
                  label={dept?.name || id}
                  size="small"
                  sx={{
                    backgroundColor: dept?.color || undefined,
                    color: dept?.color ? "#fff" : undefined,
                  }}
                />
              );
            })}
          </Box>
        )}
      >
        {departments.map((department) => (
          <MenuItem key={department.id} value={department.id}>
            <Checkbox
              checked={selectedDepartmentIds.indexOf(department.id) > -1}
            />
            <Box display="flex" alignItems="center" gap={1}>
              {department.color && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: department.color,
                  }}
                />
              )}
              <Typography>{department.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default DepartmentSelector;
